/**
 * Locale formatting for design-system components (Foundations: "Numbers, money and dates by
 * language"). Format by the page's language, never by where the data came from:
 *
 *              English        Finnish / Swedish
 *   number     19,025         19 025
 *   percent    21%            21 %
 *   money      €2,960         2 960 €
 *   range      €3,000–3,500   3 000–3 500 €
 *   date       11 Oct 2026    11.10.2026
 *
 * Finnish and Swedish separators are no-break spaces, so a figure never wraps. A negative or signed
 * figure uses the true minus sign U+2212 in every language (English included), never a hyphen.
 */

export type DsLocale = 'en' | 'fi' | 'sv';

const INTL_LOCALE: Record<DsLocale, string> = { en: 'en-GB', fi: 'fi-FI', sv: 'sv-FI' };
const NBSP = ' ';

// Some engines use a narrow no-break space (U+202F) in fi/sv; normalise to one character.
const tidy = (s: string) => s.replace(/[  ]/g, NBSP);
// English writes the sign as a hyphen-minus; fi and sv already use U+2212. A hyphen only ever appears in a formatted figure as its sign.
const MINUS = '\u2212';
const figure = (s: string) => tidy(s).replace(/-/g, MINUS);

export function formatNumber(value: number, locale: DsLocale, options: Intl.NumberFormatOptions = {}): string {
  return figure(new Intl.NumberFormat(INTL_LOCALE[locale], { maximumFractionDigits: 0, ...options }).format(value));
}

/** `percent` is 0 to 100 (21 -> "21%" / "21 %"). */
export function formatPercent(percent: number, locale: DsLocale, fractionDigits = 0): string {
  return figure(
    new Intl.NumberFormat(INTL_LOCALE[locale], { style: 'percent', maximumFractionDigits: fractionDigits }).format(
      percent / 100,
    ),
  );
}

export function formatMoney(amount: number, locale: DsLocale, currency = 'EUR'): string {
  return figure(
    new Intl.NumberFormat(INTL_LOCALE[locale], { style: 'currency', currency, maximumFractionDigits: 0 }).format(
      amount,
    ),
  );
}

/** A pay range with one currency sign: "€3,000–3,500" / "3 000–3 500 €". */
export function formatMoneyRange(min: number, max: number, locale: DsLocale, currency = 'EUR'): string {
  if (min === max) return formatMoney(min, locale, currency);
  const lo = formatNumber(min, locale);
  const hi = formatNumber(max, locale);
  const sample = formatMoney(1, locale, currency);
  const signFirst = sample.indexOf('1') > 0;
  const sign = sample.replace(/[\d\s ]/g, '');
  return signFirst ? `${sign}${lo}–${hi}` : `${lo}–${hi}${NBSP}${sign}`;
}

export function formatDate(date: Date, locale: DsLocale): string {
  const options: Intl.DateTimeFormatOptions =
    locale === 'en'
      ? { day: 'numeric', month: 'short', year: 'numeric' }
      : { day: 'numeric', month: 'numeric', year: 'numeric' };
  return tidy(date.toLocaleDateString(INTL_LOCALE[locale], options));
}

/*
 * Rounding to what the sample supports (guideline 12, Presenting data). A figure with more digits
 * than its sample can back up is a claim the data does not make.
 */

/** Pay rests on few values below this many, so it rounds coarser. */
export const PAY_SAMPLE_FINE = 30;

/** Pay to the nearest 100 € on fewer than 30 values, to the nearest 50 € on 30 or more. */
export function roundPay(value: number, sample: number): number {
  const step = sample < PAY_SAMPLE_FINE ? 100 : 50;
  return Math.round(value / step) * step;
}

/** A share (0 to 100) as a whole percent, or with one decimal when under 10%. */
export function roundShare(percent: number): number {
  return Math.abs(percent) < 10 ? Math.round(percent * 10) / 10 : Math.round(percent);
}

/** A count in prose: over 1,000 to the nearest 10 ("19,391" -> 19,390), otherwise as it is. */
export function roundCountForProse(count: number): number {
  return Math.abs(count) > 1000 ? Math.round(count / 10) * 10 : Math.round(count);
}

/** `roundPay`, written as money: formatPayForSample(2961, 18, 'en') is "€3,000". */
export function formatPayForSample(value: number, sample: number, locale: DsLocale, currency = 'EUR'): string {
  return formatMoney(roundPay(value, sample), locale, currency);
}

/** `roundShare`, written as a percent: "4.5%" under 10%, "21%" above. */
export function formatShare(percent: number, locale: DsLocale): string {
  return formatPercent(roundShare(percent), locale, Math.abs(percent) < 10 ? 1 : 0);
}

/** `roundCountForProse`, written as a number. */
export function formatCountForProse(count: number, locale: DsLocale): string {
  return formatNumber(roundCountForProse(count), locale);
}
